import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import {
  Button,
  ClickableProductImage,
  ConfirmDeleteModal,
  ImagePreviewModal,
  Pagination,
  SideDrawer,
  SkeletonText,
  SkeletonBox,
  TableChip,
} from '@/components';
import { parseApiError } from '@/common/utils';
import {
  DashboardPage,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
} from '../../components/PremiumDesign';
import {
  useQuotation,
  useQuotationProducts,
  useDeleteQuotationProduct,
  useQuotationPdfTemplates,
} from '../hooks';
import {
  formatQuoteMoney,
  showDeliveryLine,
  getCompanyId,
  resolveQuotationMediaUrl,
  sortQuotationProducts,
} from '../utils';
import { QuotationPdfActions } from '../components/QuotationPdfActions';
import { QuotationPageHeader } from '../components/QuotationPageHeader';
import {
  QuotationProductDrawer,
  QuotationProductDetailModal,
  QuotationActivityTimeline,
} from '../components';
import { pdfTemplateLabel } from '../components/PdfTemplatePicker';
import { QuotationForm } from '../components/QuotationForm';
import { PlusIcon, PencilIcon, TrashIcon } from '../../components/Icons';

// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatDateTime(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

// ─── Loading skeleton ──────────────────────────────────────────────────────────
function PageSkeleton() {
  const bar = 'bg-surface-mint animate-pulse rounded';
  return (
    <DashboardPage className="q-page">
      <div className="w-full flex flex-col gap-4">
        {/* header skeleton */}
        <div className="flex items-center justify-between gap-3 pb-3 border-b border-border-default">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${bar}`} />
            <div className="flex flex-col gap-1.5">
              <div className={`h-5 w-40 ${bar}`} />
              <div className={`h-3 w-24 ${bar}`} />
            </div>
          </div>
          <div className={`h-9 w-28 rounded-lg ${bar}`} />
        </div>
        {/* two-column skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-[10px] items-start w-full min-w-0">
          <div className="bg-[#F5F5F5] rounded-[16px] p-[14px] sm:p-[14px] flex flex-col gap-[14px] min-w-0">
            {[1, 2].map((s) => (
              <div key={s} className="flex flex-col gap-3">
                <div className={`h-5 w-28 ${bar}`} />
                <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className={`h-3 w-14 ${bar}`} />
                      <div className={`h-3 w-24 ${bar}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#F5F5F5] rounded-[16px] p-[14px] sm:p-[14px] flex flex-col gap-[14px] min-w-0 w-full">
            <div className="flex flex-col gap-3">
              <div className={`h-5 w-28 ${bar}`} />
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3">
                <div className="rounded-xl border border-surface-mint overflow-hidden">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-4 py-3 border-b border-surface-mint last:border-0"
                    >
                      <div className={`h-3 w-5 rounded ${bar}`} />
                      <SkeletonBox className="w-[56px] h-[56px] rounded-[10px] shrink-0" />
                      <div className="flex-1 flex flex-col gap-1.5">
                        <SkeletonText width="w-32" height="h-3" />
                        <SkeletonText width="w-20" height="h-2.5" />
                      </div>
                      <SkeletonText width="w-12" height="h-3" />
                      <SkeletonText width="w-8" height="h-3" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardPage>
  );
}

const PAGE_SIZE = 10;

export function QuotationDetailPage() {
  const { quotationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = getCompanyId();
  const { can } = usePermissions();
  const canEdit = can(PERMISSIONS.QUOTATIONS.UPDATE);

  const [page, setPage] = useState(1);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [productDrawerState, setProductDrawerState] = useState({
    isOpen: false,
    product: null,
    productId: null,
  });
  const [detailDrawerState, setDetailDrawerState] = useState({
    isOpen: false,
    product: null,
    productId: null,
  });
  const [isEditTemplateOpen, setIsEditTemplateOpen] = useState(false);

  const {
    data: quotation,
    isLoading,
    isFetching,
    isError,
  } = useQuotation({ companyId, quotationId });

  const { data: pdfTemplatesData } = useQuotationPdfTemplates(companyId);
  const pdfTemplates = Array.isArray(pdfTemplatesData)
    ? pdfTemplatesData
    : pdfTemplatesData?.results || [];

  const { data: productsData, isLoading: isProductsLoading } =
    useQuotationProducts({
      companyId,
      quotationId,
      params: { page, page_size: PAGE_SIZE },
    });

  const deleteProductMutation = useDeleteQuotationProduct();

  const allProducts = useMemo(() => {
    const nested = Array.isArray(quotation?.products)
      ? quotation.products
      : null;
    const listed = Array.isArray(productsData)
      ? productsData
      : productsData?.results || null;
    return sortQuotationProducts(nested || listed || []);
  }, [quotation?.products, productsData]);

  const isServerPaginated =
    typeof productsData === 'object' &&
    !Array.isArray(productsData) &&
    Array.isArray(productsData?.results) &&
    typeof productsData?.count === 'number';

  const totalCount = isServerPaginated
    ? productsData.count
    : allProducts.length;

  const displayedProducts = useMemo(() => {
    if (isServerPaginated) {
      return sortQuotationProducts(productsData.results);
    }
    const start = (page - 1) * PAGE_SIZE;
    return allProducts.slice(start, start + PAGE_SIZE);
  }, [isServerPaginated, productsData?.results, allProducts, page]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalCount, page]);

  useLayoutEffect(() => {
    const el = document.getElementById('dashboard-scroll-container');
    if (el) el.scrollTop = 0;
  }, [quotationId]);

  // Support direct URL actions (e.g. ?action=create-product, ?editProduct=123, or ?edit=true)
  useEffect(() => {
    const action = searchParams.get('action');
    const editProductId = searchParams.get('editProduct');
    const isEdit = searchParams.get('edit') === 'true';

    if (isEdit) {
      setIsEditTemplateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    } else if (action === 'create-product') {
      setProductDrawerState({ isOpen: true, product: null, productId: null });
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    } else if (editProductId) {
      const found = allProducts.find(
        (p) => String(p.id) === String(editProductId),
      );
      setProductDrawerState({
        isOpen: true,
        product: found || null,
        productId: editProductId,
      });
      const next = new URLSearchParams(searchParams);
      next.delete('editProduct');
      setSearchParams(next, { replace: true });
    } else {
      const viewProductId =
        searchParams.get('product') || searchParams.get('viewProduct');
      if (viewProductId) {
        const found = allProducts.find(
          (p) => String(p.id) === String(viewProductId),
        );
        setDetailDrawerState({
          isOpen: true,
          product: found || null,
          productId: viewProductId,
        });
        const next = new URLSearchParams(searchParams);
        next.delete('product');
        next.delete('viewProduct');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, allProducts, setSearchParams]);

  const handleDeleteProduct = () => {
    if (!deletingProductId) return;
    deleteProductMutation.mutate(
      { companyId, quotationId, productId: deletingProductId },
      {
        onSuccess: () => {
          toast.success('Product removed.');
          setDeletingProductId(null);
        },
        onError: (err) => {
          toast.error(
            parseApiError(err).message || 'Failed to remove product.',
          );
          setDeletingProductId(null);
        },
      },
    );
  };

  if (isLoading) return <PageSkeleton />;

  if (isError || !quotation) {
    return (
      <DashboardPage className="q-page">
        <div className="w-full flex flex-col gap-4">
          <QuotationPageHeader
            title="Quotation"
            backTo="/quotations?tab=templates"
            showBorder
          />
          <p className="text-body text-text-secondary text-center py-16">
            Unable to load quotation.
          </p>
        </div>
      </DashboardPage>
    );
  }

  const taxRate = parseFloat(quotation.tax_rate || 0);
  const currency = quotation.currency || 'INR';
  const pdfBusy = isFetching || deleteProductMutation.isPending;

  return (
    <DashboardPage className="q-page">
      <div className="w-full flex flex-col gap-4">
        {/* ── Page header ── */}
        <QuotationPageHeader
          title={quotation.title || 'Quotation Detail'}
          description={quotation.quote_code || undefined}
          backTo="/quotations?tab=templates"
          className="mb-0"
          showBorder
          actions={
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button
                  type="button"
                  variant="primary"
                  icon={<PlusIcon className="w-3.5 h-3.5" />}
                  iconPosition="start"
                  onClick={() =>
                    setProductDrawerState({ isOpen: true, product: null })
                  }
                  className="h-8 px-3 text-xs"
                >
                  Add Product
                </Button>
              )}
              {canEdit && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsEditTemplateOpen(true)}
                  className="h-8 px-3 text-xs"
                >
                  Edit Template
                </Button>
              )}
            </div>
          }
        />

        {/* ── Two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 sm:gap-5 items-start w-full min-w-0">
          {/* ════ LEFT COLUMN ════ */}
          <div className="bg-[#F5F5F5] rounded-[16px] p-[14px] sm:p-[14px] flex flex-col gap-[14px] min-w-0 lg:sticky lg:top-0 overflow-hidden">
            {/* ── Quotation Info ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-[18px] font-medium text-[#50AB3D]">
                Quotation Info
              </h3>
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
                <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                  <span className="text-sm text-[#868686]">Quote No.</span>
                  <span className="text-sm font-semibold text-[#1F2241] text-right break-all max-w-[60%]">
                    {quotation.quote_code || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                  <span className="text-sm text-[#868686]">Currency</span>
                  <span className="text-sm font-semibold text-[#1F2241] text-right">
                    {currency}
                  </span>
                </div>
                {taxRate > 0 && (
                  <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                    <span className="text-sm text-[#868686]">Tax</span>
                    <span className="text-sm font-semibold text-[#1F2241] text-right">
                      {taxRate}%
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                  <span className="text-sm text-[#868686]">PDF Design</span>
                  <span className="text-sm font-semibold text-[#1F2241] text-right max-w-[60%] break-words">
                    {pdfTemplateLabel(pdfTemplates, quotation.template_key)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                  <span className="text-sm text-[#868686]">Status</span>
                  <TableChip tone={quotation.is_active ? 'success' : 'neutral'}>
                    {quotation.is_active ? 'Active' : 'Inactive'}
                  </TableChip>
                </div>
                <div className="flex items-center justify-between gap-3 min-w-0 w-full">
                  <span className="text-sm text-[#868686]">Products</span>
                  <span className="text-sm font-semibold text-[#1F2241] text-right">
                    {isProductsLoading ? '…' : totalCount}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Catalog PDF ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-[18px] font-medium text-[#50AB3D]">
                Catalog PDF
              </h3>
              <div
                className={`bg-white rounded-[12px] p-4 flex flex-col min-w-0 overflow-hidden ${pdfBusy ? 'opacity-70' : ''}`}
              >
                {/* PDF status */}
                {quotation.pdf_ready ? (
                  <div className="mb-3 text-body-sm text-primary bg-surface-mint border border-primary/20 rounded-xl px-3 py-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                    <span className="truncate">
                      {formatDateTime(quotation.pdf_generated_at) ||
                        'Ready for WhatsApp'}
                    </span>
                  </div>
                ) : (
                  <div className="mb-3 text-body-sm text-amber-800 bg-amber-50 border border-amber-200/80 rounded-xl px-3 py-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    <span>
                      {totalCount > 0
                        ? 'PDF not ready — try Regenerate'
                        : 'Add at least one product to generate the catalog.'}
                    </span>
                  </div>
                )}

                <QuotationPdfActions
                  companyId={companyId}
                  quotation={quotation}
                  productCount={totalCount}
                  canEdit={canEdit}
                  hideHeader
                  className="[&_button]:h-8 [&_button]:!px-2 sm:[&_button]:!px-3 [&_button]:!text-xs"
                />
              </div>
            </div>

            {/* ── Terms & Notes ── */}
            {showDeliveryLine(
              quotation.delivery_term,
              quotation.delivery_charge,
            ) && (
              <div className="flex flex-col gap-3">
                <h3 className="text-[18px] font-medium text-[#50AB3D]">
                  Delivery
                </h3>
                <div className="bg-white rounded-[12px] p-4 flex flex-col gap-1 min-w-0">
                  <p className="text-sm text-[#1F2241]">
                    Delivery Terms: {quotation.delivery_term || 'Delivery'}
                  </p>
                  <p className="text-sm text-[#1F2241]">
                    Amount:{' '}
                    {formatQuoteMoney(
                      quotation.delivery_charge,
                      quotation.currency,
                    )}
                  </p>
                </div>
              </div>
            )}
            {quotation.terms_and_conditions && (
              <div className="flex flex-col gap-3">
                <h3 className="text-[18px] font-medium text-[#50AB3D]">
                  Terms &amp; Conditions
                </h3>
                <div className="bg-white rounded-[12px] p-4 flex flex-col min-w-0">
                  <p className="text-sm text-[#1F2241] leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">
                    {quotation.terms_and_conditions}
                  </p>
                </div>
              </div>
            )}
            {quotation.notes && (
              <div className="flex flex-col gap-3">
                <h3 className="text-[18px] font-medium text-[#50AB3D]">
                  Internal Notes
                </h3>
                <div className="bg-white rounded-[12px] p-4 flex flex-col min-w-0">
                  <p className="text-sm text-[#1F2241] leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">
                    {quotation.notes}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ════ RIGHT COLUMN ════ */}
          <div className="bg-[#F5F5F5] rounded-[16px] p-[14px] sm:p-[14px] flex flex-col gap-[14px] min-w-0 w-full">
            {/* ── Products section ── */}
            <div className="flex flex-col gap-3">
              {/* Section header */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <h3 className="text-[18px] font-medium text-[#50AB3D]">
                    Products
                  </h3>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#EBF4EA] text-[#50AB3D]">
                    {isProductsLoading ? '…' : totalCount}
                  </span>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() =>
                      setProductDrawerState({ isOpen: true, product: null })
                    }
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-[10px] text-sm font-medium text-white transition-colors hover:brightness-95 focus:outline-none"
                    style={{ backgroundColor: '#50AB3D' }}
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Product
                  </button>
                )}
              </div>

              <div className="bg-white rounded-[12px] p-4 sm:p-5 flex flex-col min-w-0">
                {isProductsLoading && !displayedProducts.length ? (
                  /* Loading state */
                  <div className="rounded-xl border border-surface-mint overflow-hidden">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-4 py-3 border-b border-surface-mint last:border-0"
                      >
                        <div className="bg-surface-mint animate-pulse rounded h-3 w-5 text-center" />
                        <SkeletonBox className="w-[56px] h-[56px] rounded-[10px] shrink-0" />
                        <div className="flex-1 flex flex-col gap-1.5">
                          <SkeletonText width="w-32" height="h-3" />
                          <SkeletonText width="w-20" height="h-2.5" />
                        </div>
                        <SkeletonText width="w-12" height="h-3" />
                        <SkeletonText width="w-8" height="h-3" />
                      </div>
                    ))}
                  </div>
                ) : totalCount === 0 ? (
                  /* Empty state */
                  <div className="py-14 flex flex-col items-center gap-3 text-center">
                    <p className="text-body font-semibold text-text-primary">
                      No products yet
                    </p>
                    {canEdit && (
                      <>
                        <p className="text-body-sm text-text-secondary">
                          Add products to create the quotation PDF.
                        </p>
                        <Button
                          type="button"
                          variant="primary"
                          icon={<PlusIcon className="w-3.5 h-3.5" />}
                          iconPosition="start"
                          onClick={() =>
                            setProductDrawerState({
                              isOpen: true,
                              product: null,
                            })
                          }
                          className="h-8 px-4 text-xs mt-1"
                        >
                          Add Product
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  /* Product table */
                  <div className="-mx-4 sm:-mx-5 overflow-x-auto custom-scrollbar">
                    <div className="inline-block min-w-full align-middle px-4 sm:px-5">
                      <div className="rounded-xl border border-surface-mint overflow-hidden">
                        <DashboardTable className="w-full">
                          <DashboardTableHeader>
                            <th className="py-3 px-3 font-semibold text-center w-[44px] text-overline uppercase tracking-wider text-surface-mint !rounded-l-[10px]">
                              #
                            </th>
                            <th className="py-3 px-4 font-semibold text-left text-overline uppercase tracking-wider text-surface-mint">
                              Product
                            </th>
                            <th className="py-3 px-4 font-semibold text-right w-[130px] text-overline uppercase tracking-wider text-surface-mint">
                              Price
                            </th>
                            <th className="py-3 px-3 font-semibold text-center w-[72px] text-overline uppercase tracking-wider text-surface-mint">
                              Tax
                            </th>
                            <th className="py-3 px-3 font-semibold text-center w-[64px] text-overline uppercase tracking-wider text-surface-mint">
                              MOQ
                            </th>
                            {canEdit && (
                              <th className="py-3 px-4 font-semibold text-right w-[80px] text-overline uppercase tracking-wider text-surface-mint !rounded-r-[10px]">
                                Actions
                              </th>
                            )}
                          </DashboardTableHeader>
                          <DashboardTableBody>
                            {displayedProducts.map((product, index) => {
                              const thumb = resolveQuotationMediaUrl(
                                product.effective_image_url ||
                                  product.image_url,
                              );
                              return (
                                <DashboardTableRow
                                  key={product.id}
                                  onClick={() =>
                                    setDetailDrawerState({
                                      isOpen: true,
                                      product,
                                      productId: product.id,
                                    })
                                  }
                                  className="group cursor-pointer hover:bg-surface-mint-soft transition-colors"
                                >
                                  {/* Order number */}
                                  <DashboardTableCell
                                    align="center"
                                    className="w-[44px] !py-3 !px-3"
                                  >
                                    <span className="text-body-sm font-bold text-primary">
                                      {product.order ??
                                        (page - 1) * PAGE_SIZE + index + 1}
                                    </span>
                                  </DashboardTableCell>

                                  {/* Product: Thumbnail + Name/Badges/SKU */}
                                  <DashboardTableCell className="!py-3 !px-4 whitespace-normal">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <ClickableProductImage
                                        imageUrl={thumb}
                                        productName={product.name}
                                        onClick={
                                          thumb
                                            ? () =>
                                                setPreviewImage({
                                                  url: thumb,
                                                  name: product.name,
                                                })
                                            : undefined
                                        }
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <p className="text-body font-semibold text-text-primary group-hover:text-primary transition-colors truncate">
                                            {product.name}
                                          </p>
                                          {product.is_available === false && (
                                            <TableChip tone="neutral">
                                              Unavailable
                                            </TableChip>
                                          )}
                                        </div>
                                        {product.sku && (
                                          <p className="text-body-sm text-text-secondary mt-0.5">
                                            SKU: {product.sku}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </DashboardTableCell>

                                  {/* Price */}
                                  <DashboardTableCell
                                    align="right"
                                    className="w-[130px] !py-3 !px-4 text-body font-semibold text-text-primary tabular-nums whitespace-nowrap"
                                  >
                                    {formatQuoteMoney(product.price, currency)}
                                  </DashboardTableCell>

                                  <DashboardTableCell
                                    align="center"
                                    className="w-[72px] !py-3 !px-3 text-body-sm text-text-secondary tabular-nums"
                                  >
                                    {product.tax_rate != null &&
                                    product.tax_rate !== ''
                                      ? `${product.tax_rate}%`
                                      : '0%'}
                                  </DashboardTableCell>

                                  {/* MOQ */}
                                  <DashboardTableCell
                                    align="center"
                                    className="w-[64px] !py-3 !px-3 text-body-sm text-text-secondary"
                                  >
                                    {product.moq || 1}
                                  </DashboardTableCell>

                                  {/* Actions */}
                                  {canEdit && (
                                    <DashboardTableCell
                                      align="right"
                                      className="w-[80px] !py-3 !px-4"
                                    >
                                      <div className="flex items-center justify-end gap-1 shrink-0">
                                        <button
                                          type="button"
                                          aria-label="Edit product"
                                          className="p-1.5 rounded-lg hover:bg-surface-mint text-primary transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setProductDrawerState({
                                              isOpen: true,
                                              product,
                                              productId: product.id,
                                            });
                                          }}
                                        >
                                          <PencilIcon className="w-[14px] h-[14px]" />
                                        </button>
                                        <button
                                          type="button"
                                          aria-label="Delete product"
                                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDeletingProductId(product.id);
                                          }}
                                        >
                                          <TrashIcon className="w-[14px] h-[14px]" />
                                        </button>
                                      </div>
                                    </DashboardTableCell>
                                  )}
                                </DashboardTableRow>
                              );
                            })}
                          </DashboardTableBody>
                        </DashboardTable>
                      </div>
                    </div>

                    {/* Pagination */}
                    {totalCount > PAGE_SIZE && (
                      <Pagination
                        currentPage={page}
                        totalCount={totalCount}
                        itemsPerPage={PAGE_SIZE}
                        onChange={setPage}
                        className="bg-white border-t border-surface-mint mt-0"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Activity Timeline section ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-[18px] font-medium text-[#50AB3D]">
                Activity Timeline
              </h3>
              <div className="bg-white rounded-[12px] p-4 flex flex-col min-w-0">
                <QuotationActivityTimeline
                  companyId={companyId}
                  quotationId={quotationId}
                  hideCard
                  scrollClassName="max-h-[420px]"
                />
              </div>
            </div>
          </div>
        </div>

        <ConfirmDeleteModal
          isOpen={!!deletingProductId}
          onCancel={() => setDeletingProductId(null)}
          onConfirm={handleDeleteProduct}
          isPending={deleteProductMutation.isPending}
          title="Remove Product"
          description="This product will be removed from the quotation template."
          itemName="this product"
        />

        <ImagePreviewModal
          isOpen={!!previewImage}
          imageUrl={previewImage?.url}
          title={previewImage?.name}
          onClose={() => setPreviewImage(null)}
        />

        <QuotationProductDetailModal
          isOpen={detailDrawerState.isOpen}
          product={detailDrawerState.product}
          productId={
            detailDrawerState.productId || detailDrawerState.product?.id
          }
          companyId={companyId}
          quotationId={quotationId}
          currency={currency}
          canEdit={canEdit}
          onClose={() =>
            setDetailDrawerState({
              isOpen: false,
              product: null,
              productId: null,
            })
          }
          onEdit={(prod) => {
            setDetailDrawerState({
              isOpen: false,
              product: null,
              productId: null,
            });
            setProductDrawerState({
              isOpen: true,
              product: prod,
              productId: prod?.id,
            });
          }}
          onDelete={(prodId) => {
            setDetailDrawerState({
              isOpen: false,
              product: null,
              productId: null,
            });
            setDeletingProductId(prodId);
          }}
        />

        <QuotationProductDrawer
          isOpen={productDrawerState.isOpen}
          product={productDrawerState.product}
          productId={
            productDrawerState.productId || productDrawerState.product?.id
          }
          companyId={companyId}
          quotationId={quotationId}
          onClose={() =>
            setProductDrawerState({
              isOpen: false,
              product: null,
              productId: null,
            })
          }
          onSuccess={() => {
            setProductDrawerState({
              isOpen: false,
              product: null,
              productId: null,
            });
          }}
        />

        <SideDrawer
          isOpen={isEditTemplateOpen}
          onClose={() => setIsEditTemplateOpen(false)}
          className="q-drawer"
          title="Edit Quotation"
          width="lg"
        >
          {quotation && (
            <QuotationForm
              companyId={companyId}
              quotation={quotation}
              onCancel={() => setIsEditTemplateOpen(false)}
              onSuccess={() => {
                setIsEditTemplateOpen(false);
              }}
            />
          )}
        </SideDrawer>
      </div>
    </DashboardPage>
  );
}

export default QuotationDetailPage;
